import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type PtsListSelectOption = { value: string; label: string };

type PtsListSelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: PtsListSelectOption[];
  placeholder: string;
  disabled?: boolean;
  hasError?: boolean;
  maxVisible?: number;
  loading?: boolean;
  emptyMessage?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Comma-separated values; option click toggles and keeps the menu open. */
  multiple?: boolean;
  /** When set with onOpenChange, only one dropdown stays open at a time. */
  selectId?: string;
  openSelectId?: string | null;
  onOpenSelectIdChange?: (id: string | null) => void;
  /** Slate ServiceDesk theme for Super Agent dialog dropdowns (portal menus). */
  agentTheme?: boolean;
};

const OPTION_HEIGHT_PX = 34;
const MENU_PADDING_PX = 8;
const SEARCH_BAR_HEIGHT_PX = 44;

export default function PtsListSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  hasError = false,
  maxVisible = 10,
  loading = false,
  emptyMessage = "No options available",
  searchable = false,
  searchPlaceholder = "Search…",
  multiple = false,
  selectId,
  openSelectId,
  onOpenSelectIdChange,
  agentTheme = false,
}: PtsListSelectProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const isControlled = selectId != null && onOpenSelectIdChange != null;
  const open = isControlled ? openSelectId === selectId : internalOpen;

  const setOpen = (next: boolean) => {
    if (disabled) return;
    if (isControlled) {
      onOpenSelectIdChange!(next ? selectId! : null);
    } else {
      setInternalOpen(next);
    }
  };

  const selectedValues = useMemo(
    () =>
      value
        .split(",")
        .map(part => part.trim())
        .filter(Boolean),
    [value]
  );

  const selectedLabel = useMemo(() => {
    const labels = selectedValues.map(id => {
      const fromOptions = options.find(option => option.value === id)?.label;
      if (fromOptions) return fromOptions;
      if (loading) return "Loading…";
      return id;
    });
    if (labels.length > 0) {
      return labels.join(", ");
    }
    return "";
  }, [options, selectedValues, loading]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!searchable || !query) return options;
    return options.filter(option => option.label.toLowerCase().includes(query));
  }, [options, searchable, searchQuery]);

  const menuMaxHeight =
    (searchable ? SEARCH_BAR_HEIGHT_PX : 0) +
    Math.max(Math.min(filteredOptions.length || 1, maxVisible), 1) * OPTION_HEIGHT_PX +
    MENU_PADDING_PX;

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 8;
    const maxHeight = Math.min(
      menuMaxHeight,
      window.innerHeight - rect.bottom - viewportPadding
    );

    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 2147483642,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open, filteredOptions.length, menuMaxHeight, searchable]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      return;
    }
    if (!searchable) return;
    const timer = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", handleOutside);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [open, isControlled, selectId]);

  const menu = open ? (
    <div
      ref={menuRef}
      className={`sd-pts-list-select-menu sd-pts-list-select-menu--portal${
        agentTheme ? " sd-pts-list-select-menu--agent" : ""
      }`}
      style={menuStyle}
    >
      {searchable && !loading && options.length > 0 && (
        <div className="sd-pts-list-select-search">
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            placeholder={searchPlaceholder}
            className="sd-pts-list-select-search-input"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      )}
      <ul className="sd-pts-list-select-options" role="listbox">
        {loading ? (
          <li className="sd-pts-list-select-empty">Loading…</li>
        ) : options.length === 0 ? (
          <li className="sd-pts-list-select-empty">{emptyMessage}</li>
        ) : filteredOptions.length === 0 ? (
          <li className="sd-pts-list-select-empty">No results found</li>
        ) : (
          filteredOptions.map(option => {
            const isSelected = selectedValues.includes(option.value);
            return (
            <li key={option.value} role="option" aria-selected={isSelected}>
              <button
                type="button"
                className={`sd-pts-list-select-option${
                  isSelected ? " sd-pts-list-select-option--selected" : ""
                }`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  if (multiple) {
                    const next = isSelected
                      ? selectedValues.filter(id => id !== option.value)
                      : [...selectedValues, option.value];
                    onChange(next.join(","));
                    return;
                  }
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
            );
          })
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className="sd-pts-list-select">
      <button
        ref={triggerRef}
        type="button"
        className={`sd-pts-select sd-pts-list-select-trigger${
          open ? " sd-pts-list-select-trigger--open" : ""
        }${hasError ? " sd-pts-input--error" : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onMouseDown={e => e.preventDefault()}
        onClick={e => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <span className={selectedLabel ? "" : "sd-pts-list-select-placeholder"}>
          {selectedLabel || placeholder}
        </span>
      </button>

      {menu && createPortal(menu, document.body)}
    </div>
  );
}
